import React, {useEffect} from 'react'
import {Button, Label} from '@therms/atalaya'
import {
  RPCDocs,
  SelectEvent,
  SelectLocation,
  SelectLocationLogEntry,
  SelectRecord,
  SelectReport,
  SelectUser,
  useLocationContext,
} from '@therms/react-modules'

interface RefsByTypesFieldsProps {
  allowedRefs: RPCDocs.Reports.GetReport.v1.RefsConfig
  onRefValueChange: (field: string, value: any) => void
  value: RPCDocs.Reports.GetReport.v1.RefsByType
}

function RefsByTypesFields({
  allowedRefs,
  onRefValueChange,
  value,
}: RefsByTypesFieldsProps) {
  const locationContext = useLocationContext()

  useEffect(() => {
    if (
      locationContext?.locationId &&
      !value?.locationIds?.includes(locationContext.locationId)
    ) {
      const newLocationIds = Array.isArray(value.locationIds)
        ? [...value.locationIds, locationContext.locationId]
        : [locationContext.locationId]

      onRefValueChange('locationIds', newLocationIds)
    }
  }, [])

  return (
    <div className="gap-y-base flex flex-col">
      {allowedRefs.events && (
        <div>
          <div className="mb-3">
            <Label>Events</Label>
          </div>

          {Array.isArray(value?.eventIds) ? (
            <SelectEvent
              combineSelected
              multi
              onChangeValue={(val) => onRefValueChange('eventIds', val)}
              value={value.eventIds}
            />
          ) : (
            <Button onClick={() => onRefValueChange('eventIds', [])} subtle>
              + Add Refs
            </Button>
          )}
        </div>
      )}
      {allowedRefs.locations && (
        <div>
          <div className="mb-3">
            <Label>Locations</Label>
          </div>

          {Array.isArray(value?.locationIds) ? (
            <SelectLocation
              combineSelected
              multi
              onChangeValue={(val) => onRefValueChange('locationIds', val)}
              value={value.locationIds}
            />
          ) : (
            <Button onClick={() => onRefValueChange('locationIds', [])} subtle>
              + Add Refs
            </Button>
          )}
        </div>
      )}
      {allowedRefs.locationLogEntries &&
        value.locationIds &&
        !!value.locationIds.length && (
          <div>
            <div className="mb-3">
              <Label>Location Log Entries</Label>
            </div>

            {Array.isArray(value?.locationLogEntryIds) ? (
              <SelectLocationLogEntry
                combineSelected
                disabled={!value.locationIds[0]}
                hint={
                  !value.locationIds[0] ? 'Select a Location Ref' : undefined
                }
                locationId={value.locationIds?.[0]}
                multi
                onChangeValue={(val) =>
                  onRefValueChange('locationLogEntryIds', val)
                }
                value={value.locationLogEntryIds}
              />
            ) : (
              <Button
                onClick={() => onRefValueChange('locationLogEntryIds', [])}
                subtle
              >
                + Add Refs
              </Button>
            )}
          </div>
        )}
      {allowedRefs.records && (
        <div>
          <div className="mb-3">
            <Label>Records</Label>
          </div>

          {Array.isArray(value?.recordIds) ? (
            <SelectRecord
              combineSelected
              multi
              onChangeValue={(val) => onRefValueChange('recordIds', val)}
              value={value.recordIds}
            />
          ) : (
            <Button onClick={() => onRefValueChange('recordIds', [])} subtle>
              + Add Refs
            </Button>
          )}
        </div>
      )}
      {allowedRefs.reports && (
        <div>
          <div className="mb-3">
            <Label>Reports</Label>
          </div>

          {Array.isArray(value?.reportIds) ? (
            <SelectReport
              combineSelected
              multi
              onChangeValue={(val) => onRefValueChange('reportIds', val)}
              value={value.reportIds}
            />
          ) : (
            <Button onClick={() => onRefValueChange('reportIds', [])} subtle>
              + Add Refs
            </Button>
          )}
        </div>
      )}
      {allowedRefs.users && (
        <div>
          <div className="mb-3">
            <Label>Users</Label>
          </div>

          {Array.isArray(value?.userIds) ? (
            <SelectUser
              combineSelected
              rolesFilter={['ops']}
              multi
              onChangeValue={(val) => onRefValueChange('userIds', val)}
              value={value.userIds}
            />
          ) : (
            <Button onClick={() => onRefValueChange('userIds', [])} subtle>
              + Add Refs
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export {RefsByTypesFields}
